import { gql, LazyQueryHookOptions, TypedDocumentNode, useLazyQuery } from "@apollo/client";

import {
  SearchCasesQuery,
  SearchCasesQueryVariables,
} from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { SearchParams } from "src/graphql/anti-fraud/queries/typings.ts";
import { useFetchQuery } from "src/graphql/utils";
import { mapToCaseSummary } from "src/mappers/case.mappers";
import { isEmpty } from "src/utils/arrays.utils.ts";

import { caseSummaryFragment } from "../../fragments/CaseSummaryFragment";

export const searchCasesQuery = gql`
  query SearchCases(
    $searchString: String
    $offset: Int = 0
    $limit: Int!
    $onlyScored: Boolean
    $caseTypeCodes: [String!]
    $sorting: CaseSortingOrder
  ) {
    caseSearch(
      input: {
        searchString: $searchString
        pagination: { offset: $offset, limit: $limit }
        onlyScored: $onlyScored
        caseTypeCodes: $caseTypeCodes
        sorting: $sorting
      }
    ) {
      cases {
        ...CaseSummaryFragment
      }
      pageInfo {
        totalCount
        offset
        limit
      }
    }
  }
  ${caseSummaryFragment}
` as TypedDocumentNode<SearchCasesQuery, SearchCasesQueryVariables>;

// Defines a search query hook for cases
// To be used in a useSearch hook
export const useSearchCasesQuery = ({
  searchTerm,
  offset,
  limit,
  onlyScored,
  caseTypeCodes,
  sorting,
}: SearchParams & { onlyScored?: boolean; caseTypeCodes?: string[] }) => {
  // if searchString is empty, send undefined to the query
  return useFetchQuery(searchCasesQuery, {
    queryOptions: {
      variables: {
        searchString: searchTerm && searchTerm?.length > 0 ? searchTerm : null,
        offset,
        limit,
        onlyScored,
        caseTypeCodes,
        sorting: {
          source: "CASE_PROPERTY",
          path: sorting![0].criterion,
          direction: sorting![0].order,
        },
      } as SearchCasesQueryVariables,
      fetchPolicy: "network-only",
    },
    toData: (data) =>
      data?.caseSearch && {
        cases: data.caseSearch.cases.map(mapToCaseSummary),
        pageInfo: data.caseSearch.pageInfo,
      },
    isEmpty: (data) => isEmpty(data?.caseSearch.cases),
    uiErrors: {
      fetchError: "FETCH-CASES-ERROR",
    },
  });
};

export const useLazySearchCasesQuery = (
  options?: LazyQueryHookOptions<SearchCasesQuery, SearchCasesQueryVariables>
) => {
  return useLazyQuery(searchCasesQuery, options);
};
